// The Audit tab's "Reject reason" column, in words.
//
// The stored value is a machine code — `operator_rejected`, `guard_max_delta` —
// and the column used to print it raw. The code has to stay visible (it is what
// `?reject_reason=` filters on, and what an operator greps the logs by), so it
// keeps its own line underneath; this module produces the sentence above it.
//
// The numbers come from two places, and neither is stored on the audit row:
//
//   - the scores are the row's own `submitted_score` / `current_score`;
//   - the limits, the operator and the sort come from the board's CURRENT
//     config, already loaded by the page. If an operator changed a limit after
//     the rejection, the old row explains itself against the new number. That
//     is a deliberate trade — the alternative is three more audit columns —
//     and it is why the code below never phrases a limit as "the limit at the
//     time".
//
// A sentence is built as tokens rather than as a string so the numbers can be
// marked up (mono, semibold) without the caller parsing text back apart, and so
// a plain-text version for `title=` falls out of the same description.

import type { ReactNode } from "react";

import type { AuditEntry, Leaderboard } from "../types";
import styles from "./AuditTab.module.css";

/** A number the sentence highlights (mono, semibold). */
interface NumToken {
  num: string;
}
/** A config value quoted verbatim — an operator name (mono, regular). */
interface OpToken {
  op: string;
}
type Token = string | NumToken | OpToken;

const NUMBER_FMT = new Intl.NumberFormat("en-US");

function num(n: number): NumToken {
  return { num: NUMBER_FMT.format(n) };
}

function signed(n: number): NumToken {
  return { num: `${n > 0 ? "+" : ""}${NUMBER_FMT.format(n)}` };
}

/**
 * Whether the event left no score behind — what the "✗" in the Score column
 * means, and what the Rejected filter selects.
 *
 * Go-truth: `applied_score` is the score the row ended up with, written on
 * exactly the events that changed one. A set `reject_reason` is deliberately
 * NOT part of this: `admin_adjusted` carries both a reason (it was an
 * operator, not a submit) and an applied_score, so reading the reason as a
 * rejection would draw a red "900 → ✗" over a score the admin successfully
 * wrote.
 *
 * It lives here, beside the rest of the audit row's reading, because the two
 * audit tables — the Audit tab and the Overview card — render the same row and
 * had this predicate twice.
 */
export function isRejected(row: AuditEntry): boolean {
  return row.applied_score === undefined || row.applied_score === null;
}

/**
 * `admin_deleted` / `admin_adjusted` sit in this column but are not
 * rejections — they are things an operator did. They read in the neutral text
 * colour, and the Score cell's "sent N" tail is not theirs either: an admin
 * submitted nothing, and on an adjustment `submitted_score` is a copy of the
 * score that was already there.
 */
export function isAdminAction(row: AuditEntry): boolean {
  return (
    row.reject_reason === "admin_deleted" ||
    row.reject_reason === "admin_adjusted"
  );
}

/**
 * The two guards that fire before the transaction opens, and so never read
 * the entity's score row: their audit rows carry no `current_score` at all.
 * It is the one case where the Score cell can say WHY it has no "before" —
 * every other row missing the column is simply older than migration 0058.
 */
export function isPreTransactionalGuard(row: AuditEntry): boolean {
  return (
    row.reject_reason === "guard_max_score" ||
    row.reject_reason === "guard_interval"
  );
}

/** Which of two equal-ranking words the operator keeps, given the sort. */
function keepsWord(lb: Leaderboard): string {
  return (lb.operator === "best") === (lb.sort === "desc")
    ? "higher"
    : "lower";
}

function operatorRejected(row: AuditEntry, lb: Leaderboard): Token[] {
  const lead = lb.operator === "worst" ? "Not worse" : "Not better";
  const tail: Token[] = [
    " — ",
    { op: lb.operator },
    ` keeps the ${keepsWord(lb)} score`,
  ];
  if (row.current_score === undefined || row.current_score === null) {
    return [`${lead} than the stored score`, ...tail];
  }
  return [`${lead} than the stored `, num(row.current_score), ...tail];
}

function guardMaxDelta(row: AuditEntry, lb: Leaderboard): Token[] {
  const limit = lb.guards?.max_delta_per_submit;
  const cap: Token[] =
    limit === undefined || limit === null
      ? [" is over the max delta"]
      : [" is over the ", num(limit), " max delta"];
  if (row.current_score === undefined || row.current_score === null) {
    return ["Jump from the stored score", ...cap];
  }
  return [
    "Jump of ",
    signed(row.submitted_score - row.current_score),
    " from the stored ",
    num(row.current_score),
    ...cap,
  ];
}

function guardMaxScore(lb: Leaderboard): Token[] {
  const limit = lb.guards?.max_score_per_submit;
  if (limit === undefined || limit === null) {
    return ["Above the max score allowed per submit"];
  }
  return ["Above the ", num(limit), " max score allowed per submit"];
}

function guardInterval(lb: Leaderboard): Token[] {
  const secs = lb.guards?.min_interval_sec;
  if (secs === undefined || secs === null) {
    return ["Submitted again inside the minimum interval"];
  }
  return [
    "Submitted again inside the ",
    { num: `${NUMBER_FMT.format(secs)}s` },
    " minimum interval",
  ];
}

/**
 * The team-battle board's own two refusals (guild_score_source =
 * member_contribution). Neither is about the SCORE that arrived, which is why
 * neither reads a number off the row: they are about who sent it and about the
 * team behind them.
 *
 * The threshold, like every other limit in this file, is the board's CURRENT
 * one — see the note at the top.
 */
function teamTooSmall(lb: Leaderboard): Token[] {
  const min = lb.min_guild_members;
  if (min === undefined || min === null || min === 0) {
    return ["The team is under the minimum size, so it cannot enter the battle"];
  }
  return [
    "The team has fewer than ",
    num(min),
    " members, so it cannot enter the battle yet",
  ];
}

/**
 * The member is in the guild but not in its run at this cycle, so there is no
 * contribution of theirs for the team's score to be the sum of.
 */
function notParticipating(): Token[] {
  return ["Not one of the team's participants in this cycle"];
}

/**
 * The sentence for a row's reject_reason, or null for a code this build does
 * not know — a forward-compatible backend, or a row from a future migration.
 * The caller falls back to showing the code alone, which is where the column
 * started.
 */
function reasonTokens(row: AuditEntry, lb: Leaderboard): Token[] | null {
  switch (row.reject_reason) {
    case "operator_rejected":
      return operatorRejected(row, lb);
    case "guard_max_delta":
      return guardMaxDelta(row, lb);
    case "guard_max_score":
      return guardMaxScore(lb);
    case "guard_interval":
      return guardInterval(lb);
    case "team_too_small":
      return teamTooSmall(lb);
    case "not_participating":
      return notParticipating();
    case "admin_adjusted":
      return ["Score overwritten by an admin"];
    case "admin_deleted":
      return ["Score row deleted by an admin"];
    default:
      return null;
  }
}

/** One reason, ready to render: rich text plus its plain-text twin. */
export interface DescribedReason {
  /** The sentence with its numbers marked up. */
  node: ReactNode;
  /** The same sentence as flat text — for `title` on the ellipsised line. */
  plain: string;
}

export function describeRejectReason(
  row: AuditEntry,
  lb: Leaderboard,
): DescribedReason | null {
  const tokens = reasonTokens(row, lb);
  if (!tokens) return null;
  return {
    node: tokens.map((t, i) => {
      if (typeof t === "string") return t;
      if ("num" in t) {
        return (
          <span key={i} className={styles.reasonNum}>
            {t.num}
          </span>
        );
      }
      return (
        <span key={i} className={styles.reasonOp}>
          {t.op}
        </span>
      );
    }),
    plain: tokens
      .map((t) => (typeof t === "string" ? t : "num" in t ? t.num : t.op))
      .join(""),
  };
}
